/**
 * Proportional editing influence ring — camera-aligned circle around
 * the gizmo pivot showing the radius of influence in world space.
 *
 * Rendered as a standalone R3F component (NOT inside the gizmo hierarchy)
 * to avoid parent-transform distortion from objectScale/objectRotation.
 * Reads the gizmo's world-space pivot from a shared module-level ref
 * written by TransformGizmo each frame.
 *
 * The world radius replicates the exact computation in storeCommandOps:
 *   worldRadius = screenToWorldRadius(proportionalRadius, objectOrigin)
 * using the same cached camera state. This component also keeps that
 * cache fresh by calling setCameraState every frame, which fixes the
 * stale-cache issue in edit mode (previously only BrushCursor updated it).
 *
 * The radius is cached and only recomputed when proportionalRadius or
 * the pivot changes — not when the camera moves. This means the ring
 * behaves like a real 3D object: it shrinks on screen when you zoom out
 * and grows when you zoom in.
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { Discard, length, mul, smoothstep, sub, uniform, uv } from "three/tsl";
import * as THREE from "three/webgpu";
import { getSceneObjectWorldTransform } from "../../app/collectionGraph.ts";
import { useStore } from "../../app/store.ts";
import { orientBillboardToCamera } from "../billboard.ts";
import { useDisposeMany } from "../hooks/useDispose.ts";
import { screenToWorldRadius, setCameraState } from "../screenRadius.ts";
import { applyGizmoRenderOrder } from "./renderOrder.ts";

// ---------------------------------------------------------------------------
// Shared state: written by TransformGizmo, read by this component
// ---------------------------------------------------------------------------

const _sharedPivot = new THREE.Vector3();
let _sharedVisible = false;

/**
 * Called by TransformGizmo each frame to publish the gizmo's world-space
 * pivot position and visibility.
 */
export function setGizmoPivotWorld(pos: THREE.Vector3, visible: boolean): void {
  _sharedPivot.copy(pos);
  _sharedVisible = visible;
}

// ---------------------------------------------------------------------------
// Visual constants
// ---------------------------------------------------------------------------

const RING_OPACITY = 0.35;
const RING_COLOR = 0xaaaaaa;
const RING_STROKE_PX = 1.5;
const GEOMETRY_EPSILON = 1e-6;
const RING_FEATHER = 0.01;
const _billboardQuat = new THREE.Quaternion();
const _worldRef = new THREE.Vector3();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProportionalInfluenceRing() {
  const groupRef = useRef<THREE.Group>(null);
  const meshRef = useRef<THREE.Mesh>(null);
  const { camera, size } = useThree();

  const proportionalEnabled = useStore((s) => s.proportionalEnabled);
  const proportionalRadius = useStore((s) => s.proportionalRadius);
  const mode = useStore((s) => s.mode);
  const editMode = useStore((s) => s.editMode);
  const sceneObjects = useStore((s) => s.sceneObjects);
  const collections = useStore((s) => s.collections);

  const inEditMode = mode === "edit" && editMode != null;
  const hasSelection = inEditMode && editMode!.selectedVertices.size > 0;
  const show = proportionalEnabled && hasSelection;

  // Resolve the edit-mode object's world origin (mirrors what storeCommandOps
  // now uses for proportional radius conversion — see `commandEnter.ts`).
  const editObj = inEditMode ? sceneObjects.get(editMode!.objectId) : null;

  const ringGeo = useMemo(() => new THREE.PlaneGeometry(2, 2), []);
  const innerRatioU = useMemo(() => (uniform as any)(0.9), []);
  const ringColorU = useMemo(() => uniform(new THREE.Color(RING_COLOR)), []);
  const ringMat = useMemo(() => {
    const centered = sub(uv(), 0.5);
    const dist = mul(length(centered), 2.0);
    const alpha = mul(
      smoothstep(innerRatioU, innerRatioU.add(RING_FEATHER), dist),
      smoothstep(1.0, 1.0 - RING_FEATHER, dist),
    );
    Discard(alpha.lessThan(0.01));

    const mat = new THREE.MeshBasicNodeMaterial();
    mat.colorNode = ringColorU;
    mat.opacityNode = mul(alpha, RING_OPACITY);
    mat.transparent = true;
    mat.depthWrite = false;
    mat.depthTest = false;
    mat.side = THREE.DoubleSide;
    return mat;
  }, [innerRatioU, ringColorU]);
  useDisposeMany(ringGeo, ringMat);

  // Cached world radius — recomputed when proportionalRadius or pivot changes.
  const cachedWorldRadius = useRef(0);
  const cachedStrokeWorldWidth = useRef(0);
  const cachedOuterRadius = useRef(0);
  const prevRadius = useRef(0);
  const prevPivot = useRef(new THREE.Vector3());

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    applyGizmoRenderOrder(group, 997);
  });

  useFrame(() => {
    // Keep the module-level camera cache fresh so that storeCommandOps
    // (which calls screenToWorldRadius without explicit camera) uses
    // up-to-date values in edit mode — not just sculpt/sketch mode.
    setCameraState(camera, size.height);

    const group = groupRef.current;
    const mesh = meshRef.current;
    if (!group || !mesh) return;

    if (!show || !_sharedVisible || !editObj) {
      group.visible = false;
      return;
    }

    group.visible = true;

    // Recompute world radius when proportionalRadius or pivot changes.
    // Uses the exact same conversion as storeCommandOps: object origin
    // as depth reference, and the same screenToWorldRadius path.
    const pivotMoved = !prevPivot.current.equals(_sharedPivot);
    const radiusChanged = prevRadius.current !== proportionalRadius;

    if (radiusChanged || pivotMoved || cachedWorldRadius.current === 0) {
      // Use the object's *world* origin (collection.transform ∘ obj local) as
      // the depth reference so the screen-to-world radius conversion matches
      // what `commandEnter.ts` computes for proportional weights. Reading
      // `editObj.position` directly would be the child-local origin, which is
      // offset from the rendered mesh when the target is a collection member.
      const wt = editMode
        ? getSceneObjectWorldTransform(
            sceneObjects,
            collections,
            editMode.objectId,
          )
        : null;
      const refPos = wt?.position ?? editObj.position;
      _worldRef.set(refPos[0], refPos[1], refPos[2]);
      const worldRadius = screenToWorldRadius(
        proportionalRadius,
        _worldRef,
        camera,
        size.height,
      );
      // The influence in world space is worldRadius (localRadius * avgScale
      // cancels back out to worldRadius). Display this directly.
      cachedWorldRadius.current = worldRadius;

      prevRadius.current = proportionalRadius;
      prevPivot.current.copy(_sharedPivot);
    }

    const strokeWorldWidth = screenToWorldRadius(
      RING_STROKE_PX,
      _sharedPivot,
      camera,
      size.height,
    );

    const outerRadius = Math.max(cachedWorldRadius.current, strokeWorldWidth);
    if (
      Math.abs(cachedStrokeWorldWidth.current - strokeWorldWidth) >
        GEOMETRY_EPSILON ||
      Math.abs(cachedOuterRadius.current - outerRadius) > GEOMETRY_EPSILON
    ) {
      const innerRadius = Math.max(outerRadius - strokeWorldWidth, 0.0001);
      innerRatioU.value = innerRadius / outerRadius;
      cachedStrokeWorldWidth.current = strokeWorldWidth;
      cachedOuterRadius.current = outerRadius;
    }

    // Position at the gizmo's world-space pivot
    group.position.copy(_sharedPivot);

    // Billboard — always face the camera
    orientBillboardToCamera(group, camera, _billboardQuat);
    mesh.scale.setScalar(cachedOuterRadius.current);
    group.scale.setScalar(1);
  });

  if (!show) return null;

  return (
    <group
      ref={groupRef}
      renderOrder={997}
      userData={{ excludeFromExport: true }}
    >
      <mesh ref={meshRef} geometry={ringGeo} material={ringMat} />
    </group>
  );
}
